import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  X, Phone, Mail, MapPin, RefreshCw, Save, Loader2, Pencil, Trash2, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore, getLocalized, useLanguageStore } from '../store';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import LocalizedField from '../components/admin/LocalizedField';
import TranslateAllButton from '../components/admin/TranslateAllButton';
import AdminHeader from '../components/admin/AdminHeader';
import { useTranslation } from '../i18n/translations';

const API = process.env.REACT_APP_BACKEND_URL;
const EMPTY_LOCALIZED = { en: '', nl: '', ro: '', ru: '' };

const coerceLocalized = (v) => {
  if (!v) return { ...EMPTY_LOCALIZED };
  if (typeof v === 'string') return { ...EMPTY_LOCALIZED, en: v };
  return { ...EMPTY_LOCALIZED, ...v };
};

const emptyForm = () => ({
  id: null,
  region: { ...EMPTY_LOCALIZED },
  representative_name: { ...EMPTY_LOCALIZED },
  phone: '',
  email: '',
  address: { ...EMPTY_LOCALIZED },
  instagram_url: '',
  whatsapp_url: '',
  showroom_hours: { ...EMPTY_LOCALIZED },
  flag_emoji: '',
  lat: '',
  lng: '',
  sort_order: 0,
  is_active: true,
});

const ContactFormModal = ({ open, onClose, token, initial, onSaved }) => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  const initialForm = () => {
    const base = emptyForm();
    if (!initial) return base;
    return {
      ...base,
      ...initial,
      region: coerceLocalized(initial.region),
      representative_name: coerceLocalized(initial.representative_name),
      address: coerceLocalized(initial.address),
      showroom_hours: coerceLocalized(initial.showroom_hours),
    };
  };
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const handleLocalized = (k) => (next) => setForm({ ...form, [k]: next });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(form.region.en || '').trim()) {
      toast.error('Region identifier (English) is required');
      return;
    }
    setSaving(true);
    try {
      const url = form.id ? `${API}/api/admin/contacts/${form.id}` : `${API}/api/admin/contacts`;
      const method = form.id ? 'PUT' : 'POST';
      const payload = { ...form };
      delete payload.id;

      ['email', 'phone', 'instagram_url', 'whatsapp_url', 'flag_emoji'].forEach((k) => {
        if (typeof payload[k] === 'string' && !payload[k].trim()) payload[k] = null;
      });
      payload.sort_order = Number(payload.sort_order) || 0;
      payload.lat = payload.lat === '' || payload.lat == null ? null : Number(payload.lat);
      payload.lng = payload.lng === '' || payload.lng == null ? null : Number(payload.lng);
      if (Number.isNaN(payload.lat)) payload.lat = null;
      if (Number.isNaN(payload.lng)) payload.lng = null;
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = typeof err.detail === 'string'
          ? err.detail
          : Array.isArray(err.detail) ? err.detail.map((d) => d.msg).join(', ') : 'Save failed';
        throw new Error(detail);
      }
      toast.success(form.id ? 'Contact blueprint updated' : 'Contact blueprint created');
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const fieldClass = "w-full px-3 py-2 border border-neutral-200 bg-white text-neutral-900 text-sm focus:outline-none focus:border-black rounded-none h-11 font-light";
  const labelClass = "block text-[10px] tracking-[0.25em] uppercase text-neutral-400 font-mono mb-2";

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-background w-full max-w-2xl max-h-[92vh] overflow-y-auto border border-neutral-200 rounded-none shadow-2xl"
        data-testid="contact-form-modal"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 px-8 py-5 flex items-start justify-between">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-neutral-400 font-mono mb-1.5">Offices Framework</p>
            <h2 className="text-2xl font-extralight tracking-tight text-neutral-900">
              {form.id ? 'Edit Office Connection' : 'Initialize Regional Office'}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 text-neutral-400 hover:text-black transition-colors" aria-label="Close" data-testid="contact-form-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5 bg-white">
          <div className="flex justify-end -mb-1">
            <TranslateAllButton
              testid="contact-translate-all"
              fields={() => [
                { value: form.region, onChange: (v) => setForm((p) => ({ ...p, region: v })) },
                { value: form.representative_name, onChange: (v) => setForm((p) => ({ ...p, representative_name: v })) },
                { value: form.address, onChange: (v) => setForm((p) => ({ ...p, address: v })) },
                { value: form.showroom_hours, onChange: (v) => setForm((p) => ({ ...p, showroom_hours: v })) },
              ]}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-3">
              <input style={{ display: 'none' }} type="text" name="chrome-autofill-dummy" />
              <LocalizedField
                label="Region / Showroom Hub Name *" required
                value={form.region}
                onChange={handleLocalized('region')}
                testid="contact-region"
                placeholder="Belgium · Brussels Hub"
              />
            </div>
            <div>
              <label className={labelClass}>Flag Emoji</label>
              <input type="text" value={form.flag_emoji || ''} onChange={handleChange('flag_emoji')}
                placeholder="🇧🇪" maxLength={4}
                className={fieldClass} data-testid="contact-flag" />
            </div>
          </div>

          <LocalizedField
            label="Atelier Representative Person Name"
            value={form.representative_name}
            onChange={handleLocalized('representative_name')}
            testid="contact-rep"
            placeholder="Oksana Fedyk"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Secure Phone Connection</label>
              <input type="text" value={form.phone || ''} onChange={handleChange('phone')}
                placeholder="+32 2 555 0123" className={fieldClass} data-testid="contact-phone" />
            </div>
            <div>
              <label className={labelClass}>Atelier Digital Email</label>
              <input type="email" value={form.email || ''} onChange={handleChange('email')}
                placeholder="brussels@fedykfurniture.com" className={fieldClass} data-testid="contact-email" />
            </div>
          </div>

          <LocalizedField
            label="Physical Showroom Address Location"
            multiline rows={2}
            value={form.address}
            onChange={handleLocalized('address')}
            testid="contact-address"
            placeholder="Rue Belliard 65, 1000 Brussels"
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Instagram URL Link</label>
              <input type="url" value={form.instagram_url || ''} onChange={handleChange('instagram_url')}
                placeholder="https://instagram.com/fedyk_be" className={fieldClass} data-testid="contact-instagram" />
            </div>
            <div>
              <label className={labelClass}>WhatsApp Concierge Link</label>
              <input type="url" value={form.whatsapp_url || ''} onChange={handleChange('whatsapp_url')}
                placeholder="https://wa.me/32255550123" className={fieldClass} data-testid="contact-whatsapp" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-neutral-100 p-1 bg-neutral-50/10">
              <LocalizedField
                label="Showroom Operating Hours Timeline"
                value={form.showroom_hours}
                onChange={handleLocalized('showroom_hours')}
                testid="contact-hours"
                placeholder="Mon-Fri 10:00-19:00 / By Appointment"
              />
            </div>
            <div>
              <label className={labelClass}>Sort Index (lower = primary hub)</label>
              <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                className={fieldClass} data-testid="contact-sort-order" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center border-t border-neutral-100 pt-4">
            <div>
              <label className={labelClass}>Latitude GPS coordinate (optional)</label>
              <input
                type="number" step="any" value={form.lat || ''}
                onChange={handleChange('lat')}
                placeholder="50.8503"
                className={fieldClass} data-testid="contact-lat"
              />
            </div>
            <div>
              <label className={labelClass}>Longitude GPS coordinate (optional)</label>
              <input
                type="number" step="any" value={form.lng || ''}
                onChange={handleChange('lng')}
                placeholder="4.3517"
                className={fieldClass} data-testid="contact-lng"
              />
            </div>
            <p className="text-[11px] text-neutral-400 font-light md:col-span-2 leading-relaxed">
              Leave coordinates blank to initialize auto-rendering map lookups. Paste explicit float data from Google Maps for millimeter precise pin positioning.
            </p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm pt-2 font-normal text-neutral-800 select-none">
            <input
              type="checkbox" checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              data-testid="contact-active"
              className="accent-black w-4 h-4 cursor-pointer"
            />
            Publish & Surface active profile on /contact
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
            <Button type="button" variant="outline" className="rounded-none h-11 px-6 text-xs uppercase tracking-wider" onClick={onClose}>
              {t?.common?.cancel || 'Cancel'}
            </Button>
            {/* ФИКС: Убран некорректный мусорный класс Stein-spin */}
            <Button type="submit" disabled={saving} className="bg-black text-white hover:bg-neutral-800 px-6 text-xs rounded-none uppercase tracking-widest h-11 transition-colors min-w-[150px]">
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" strokeWidth={1.5} /> : <Save className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />}
              {saving ? 'Saving…' : (form.id ? 'Save Changes' : 'Initialize')}
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

const AdminContactsPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);

  const userFromHook = useAuthStore((s) => s.user);
  const tokenFromHook = useAuthStore((s) => s.token);
  const logout = useAuthStore((s) => s.logout);
  const [hydrated, setHydrated] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const user = userFromHook || (hydrated ? useAuthStore.getState().user : null);
  const token = tokenFromHook || (hydrated ? useAuthStore.getState().token : null);

  useEffect(() => {
    const id = setTimeout(() => setHydrated(true), 300);
    return () => clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    const u = useAuthStore.getState().user;
    if (!u || u.role !== 'admin') {
      navigate('/admin', { replace: true, state: { from: '/admin/contacts' } });
    }
  }, [hydrated, navigate]);

  const fetchContacts = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/contacts`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401 || res.status === 403) { logout(); navigate('/admin'); return; }
      const data = await res.json();
      setContacts(data.contacts || []);
    } catch {
      toast.error('Failed to load contacts data');
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => { if (hydrated && token) fetchContacts(); }, [hydrated, token, fetchContacts]);

  const handleNew = () => { setEditing(null); setModalOpen(true); };
  const handleEdit = (c) => { setEditing(c); setModalOpen(true); };

  const handleDelete = async (c) => {
    if (!c || !c.id) return;
    const regionLabel = getLocalized(c.region || {}, 'en') || c.id;
    if (!window.confirm(`Delete "${regionLabel}" permanently? This operation is absolute.`)) return;
    try {
      const res = await fetch(`${API}/api/admin/contacts/${c.id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Contact deleted'); fetchContacts();
    } catch { toast.error('Failed to delete contact'); }
  };

  if (!hydrated || loading) return <div className="min-h-screen flex items-center justify-center font-mono text-xs text-neutral-400 bg-white">Loading regional directory…</div>;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-left font-light" data-testid="admin-contacts-page">
      <SEO title="Contacts · Admin" description="Manage contact offices" path="admin/contacts" />
      <AdminHeader active="contacts" />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-neutral-200 pb-6">
          <div>
            <p className="text-[10px] font-medium tracking-[0.2em] uppercase text-neutral-400 mb-2 font-mono">CMS · Reach</p>
            <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-black">Showrooms & Contacts</h1>
            <p className="text-neutral-500 mt-4 max-w-xl text-sm leading-relaxed font-light">
              Configure corporate showrooms, architectural nodes, regional offices, and authorized digital assistance channels.
            </p>
          </div>
          
          <div className="flex items-center gap-3 shrink-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchContacts} 
              className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium uppercase tracking-wider"
              style={{ height: '40px' }}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Refresh Hub
            </Button>
            <Button 
              variant="outline"
              size="sm"
              onClick={handleNew} 
              className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium uppercase tracking-wider"
              style={{ height: '40px' }}
              data-testid="admin-new-contact-btn"
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />New Office
            </Button>
          </div>
        </div>

        {contacts.length === 0 ? (
          <div className="py-24 text-center text-neutral-500 border border-neutral-200 bg-white rounded-none shadow-sm text-sm font-light" data-testid="admin-contacts-empty">
            <p className="text-neutral-400 mb-6 font-mono text-xs">No corporate nodes synchronized inside database environment yet.</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleNew} 
              className="h-10 bg-white text-neutral-900 border-neutral-200 text-xs rounded-none px-4 shadow-sm hover:bg-neutral-50 flex items-center transition-all font-medium mx-auto uppercase tracking-wider"
              style={{ height: '40px' }}
            >
              <Plus className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} />Initialize First Regional Contact Node
            </Button>
          </div>
        ) : (
          <div className="border border-neutral-200 divide-y divide-neutral-200 bg-white rounded-none shadow-sm" data-testid="admin-contacts-list">
            {contacts.map((c) => {
              if (!c) return null;
              const safeRegion = c.region || EMPTY_LOCALIZED;
              const safeRep = c.representative_name || EMPTY_LOCALIZED;
              const safeAddress = c.address || EMPTY_LOCALIZED;

              return (
                <div key={c.id || Math.random()} className="grid grid-cols-12 gap-4 p-5 items-center hover:bg-neutral-50/30 transition-colors" data-testid={`admin-contact-${c.id}`}>
                  <div className="col-span-12 md:col-span-3">
                    <p className="font-medium text-neutral-950 flex items-center gap-2 text-sm tracking-wide">
                      {c.flag_emoji && <span className="text-lg leading-none select-none">{c.flag_emoji}</span>}
                      {getLocalized(safeRegion, language)}
                      {!c.is_active && <span className="px-1.5 py-0.5 bg-neutral-900 text-white text-[8px] tracking-widest uppercase font-bold rounded-none ml-1">Draft</span>}
                    </p>
                    {getLocalized(safeRep, language) && (
                      <p className="text-xs text-neutral-400 mt-0.5 font-light">{getLocalized(safeRep, language)}</p>
                    )}
                  </div>
                  <div className="col-span-6 md:col-span-3 text-xs text-neutral-500 space-y-1 font-light">
                    {c.phone && <p className="flex items-center gap-1.5"><Phone className="w-3 h-3 text-neutral-300" strokeWidth={1.5} />{c.phone}</p>}
                    {c.email && <p className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-neutral-300" strokeWidth={1.5} />{c.email}</p>}
                  </div>
                  <div className="col-span-6 md:col-span-4 text-xs text-neutral-500 font-light">
                    {getLocalized(safeAddress, language) && (
                      <p className="flex items-start gap-1.5"><MapPin className="w-3 h-3 mt-0.5 shrink-0 text-neutral-300" strokeWidth={1.5} /><span className="line-clamp-2 leading-relaxed">{getLocalized(safeAddress, language)}</span></p>
                    )}
                  </div>
                  <div className="col-span-12 md:col-span-2 flex items-center gap-2 md:justify-end shrink-0">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEdit(c)} 
                      className="rounded-none text-xs h-9 bg-white text-neutral-900 border-neutral-200 transition-colors p-0 w-11"
                      data-testid={`admin-contact-edit-${c.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleDelete(c)} 
                      className="rounded-none text-xs h-9 bg-white text-red-600 border-neutral-200 transition-colors p-0 w-11 hover:bg-rose-50"
                      data-testid={`admin-contact-delete-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ContactFormModal
        key={editing?.id || 'new'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        token={token}
        initial={editing}
        onSaved={fetchContacts}
      />
    </div>
  );
};

export default AdminContactsPage;
